import {FC, useState} from 'react'
import {PageTitle} from '../../../../../_metronic/layout/core'
import {Button, Col, Row, Spinner, Card, Form, Modal, Table, Alert} from 'react-bootstrap'
import {panaAdmin} from '../../../../../services/pana'
import {toast} from 'react-toastify'

type ImportInsentifError = {
  excel_row: number
  nik: string
  email: string
  nama_penerima: string
  uraian: string
  errors: string[]
}

type ImportInsentifResult = {
  success_count: number
  error_count: number
  errors: ImportInsentifError[]
}

const ExcelManagementAdmin: FC = () => {
  const [loading, setLoading] = useState<boolean>(false)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [insentifUraian, setInsentifUraian] = useState<string>('')
  const [insentifUploadFile, setInsentifUploadFile] = useState<File | null>(null)
  const [importResult, setImportResult] = useState<ImportInsentifResult | null>(null)
  const [showResultModal, setShowResultModal] = useState<boolean>(false)

  const handleDownloadTemplate = async () => {
    setLoading(true)
    try {
      const response = await panaAdmin.downloadAssessmentExcel()

      // Backend returns: { data: { name: "...", file: "data:application/..;base64,..." } }
      let base64Data = ''
      let filename = `Assessment_Penempatan_${new Date().getTime()}.xlsx`

      if (response.data?.file) {
        // Extract base64 from data URI
        const dataURI = response.data.file
        if (dataURI.includes('base64,')) {
          base64Data = dataURI.split('base64,')[1]
        } else {
          base64Data = dataURI
        }
        
        // Use filename from response
        if (response.data.name) {
          filename = response.data.name
        }
      } else if (response.data?.data) {
        // Fallback: { data: "base64string" }
        base64Data = response.data.data
      } else if (typeof response.data === 'string') {
        // Fallback: base64 string directly
        base64Data = response.data
      } else {
        throw new Error('Invalid response format')
      }

      // Convert base64 to blob
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Template berhasil didownload')
    } catch (error: any) {
      console.error('Error downloading template:', error)
      toast.error('Gagal mendownload template: ' + (error.message || 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        toast.error('File harus berformat Excel (.xlsx atau .xls)')
        return
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 5MB')
        return
      }

      setUploadFile(file)
    }
  }

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.warning('Pilih file terlebih dahulu')
      return
    }

    setLoading(true)
    try {
      const response = await panaAdmin.uploadAssessmentExcel(uploadFile, 0) // preview=0 (save)
      console.log('Upload response:', response) // Debug log
      
      const data = response.data?.data || response.data
      const importedCount = data?.imported || data?.success_count || 0
      const errorCount = data?.error_count || 0
      
      if (importedCount > 0) {
        toast.success(`Data berhasil diimport! ${importedCount} data berhasil disimpan.`)
        
        // Reset form
        setUploadFile(null)
        const fileInput = document.getElementById('fileInput') as HTMLInputElement
        if (fileInput) fileInput.value = ''
      } else if (errorCount > 0) {
        toast.error(`Import gagal! ${errorCount} data mengalami error. Periksa format Excel.`)
      } else {
        toast.warning('Tidak ada data yang berhasil disimpan. Periksa isi file Excel.')
      }
    } catch (error: any) {
      console.error('Error uploading:', error)
      const errorMsg = error.response?.data?.meta?.message || 'Gagal mengupload file'
      toast.error(errorMsg)
    } finally {
      setLoading(false)
    }
  }

  const handleExportInsentif = async () => {
    if (!insentifUraian.trim()) {
      toast.warning('Uraian insentif harus diisi')
      return
    }

    setLoading(true)
    try {
      const response = await panaAdmin.exportInsentifExcel({
        uraian: insentifUraian,
      })

      // Backend returns: { data: { name: "...", file: "data:application/..;base64,..." } }
      let base64Data = ''
      let filename = `Insentif_ERP_${insentifUraian.replace(/\s/g, '_')}_${new Date().getTime()}.xlsx`

      if (response.data?.file) {
        // Extract base64 from data URI
        const dataURI = response.data.file
        if (dataURI.includes('base64,')) {
          base64Data = dataURI.split('base64,')[1]
        } else {
          base64Data = dataURI
        }
        
        // Use filename from response
        if (response.data.name) {
          filename = response.data.name
        }
      } else if (response.data?.data) {
        // Fallback: { data: "base64string" }
        base64Data = response.data.data
      } else if (typeof response.data === 'string') {
        // Fallback: base64 string directly
        base64Data = response.data
      } else {
        throw new Error('Invalid response format')
      }

      // Convert base64 to blob
      const byteCharacters = atob(base64Data)
      const byteNumbers = new Array(byteCharacters.length)
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
      }
      const byteArray = new Uint8Array(byteNumbers)
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })

      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)

      toast.success('Excel insentif berhasil diexport')
      setInsentifUraian('')
    } catch (error: any) {
      console.error('Error exporting insentif:', error)
      toast.error('Gagal export excel insentif: ' + (error.message || 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const handleInsentifFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      
      // Validate file type
      const validExtensions = ['.xlsx', '.xls', '.csv']
      const isValidType = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
      
      if (!isValidType) {
        toast.error('File harus berformat .xlsx, .xls, atau .csv')
        return
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Ukuran file maksimal 10MB')
        return
      }

      setInsentifUploadFile(file)
    }
  }

  const handleImportInsentif = async () => {
    if (!insentifUploadFile) {
      toast.warning('Pilih file terlebih dahulu')
      return
    }

    setLoading(true)
    try {
      const response = await panaAdmin.importInsentifExcel(insentifUploadFile)
      console.log('Import response:', response)
      
      const data = response.data?.data || response.data
      const result: ImportInsentifResult = {
        success_count: data?.success_count || 0,
        error_count: data?.error_count || 0,
        errors: data?.errors || [],
      }
      
      setImportResult(result)
      setShowResultModal(true)
      
      if (result.success_count > 0) {
        toast.success(`Import selesai! ${result.success_count} data berhasil disimpan.`)
        
        // Reset form jika tidak ada error
        if (result.error_count === 0) {
          setInsentifUploadFile(null)
          const fileInput = document.getElementById('insentifFileInput') as HTMLInputElement
          if (fileInput) fileInput.value = ''
        }
      } else if (result.error_count > 0) {
        toast.error(`Semua data gagal diimport! ${result.error_count} baris mengalami error.`)
      }
    } catch (error: any) {
      console.error('=== ERROR IMPORT INSENTIF ===')
      console.error('Error object:', error)
      console.error('Error code:', error.code)
      console.error('Error message:', error.message)
      console.error('Error desc:', error.desc)
      
      // Ekstrak error message dari berbagai kemungkinan struktur
      let errorMsg = 'Gagal mengupload file'
      
      if (error.message) {
        errorMsg = error.message
      } else if (error.desc) {
        errorMsg = error.desc
      }
      
      // Tampilkan error dengan detail untuk debugging
      toast.error(`${errorMsg}. Hubungi developer untuk cek backend.`)
      console.error('Backend error - kemungkinan bug di backend API')
    } finally {
      setLoading(false)
    }
  }

  const handleCloseResultModal = () => {
    setShowResultModal(false)
  }

  return (
    <>
      <PageTitle breadcrumbs={[]}>Kelola Excel - Admin</PageTitle>

      <Row>
        {/* Download Template */}
        <Col md={12} className='mb-5'>
          <Card>
            <Card.Header>
              <Card.Title className='fw-bold fs-3'>
                <i className='bi bi-download text-success me-2'></i>
                Download Template Excel
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <p className='text-muted mb-4'>
                Download template Excel untuk assessment penempatan. Template ini berisi data user
                PANA yang bisa diisi penilaian manual.
              </p>
              <Button variant='success' onClick={handleDownloadTemplate} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Downloading...
                  </>
                ) : (
                  <>
                    <i className='bi bi-file-earmark-excel me-2'></i>
                    Download Template
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Upload Excel */}
        <Col md={12} className='mb-5'>
          <Card>
            <Card.Header>
              <Card.Title className='fw-bold fs-3'>
                <i className='bi bi-upload text-primary me-2'></i>
                Upload Excel Penilaian Manual
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <p className='text-muted mb-4'>
                Upload file Excel yang sudah diisi untuk import penilaian manual. Pastikan format
                sesuai dengan template.
              </p>

              <Form.Group className='mb-3'>
                <Form.Label>Pilih File Excel *</Form.Label>
                <Form.Control
                  id='fileInput'
                  type='file'
                  accept='.xlsx,.xls'
                  onChange={handleFileChange}
                />
                <Form.Text className='text-muted'>
                  Format: .xlsx atau .xls | Max: 5MB
                </Form.Text>
              </Form.Group>

              {uploadFile && (
                <div className='alert alert-info mb-4'>
                  <i className='bi bi-file-earmark-check me-2'></i>
                  <strong>File dipilih:</strong> {uploadFile.name} (
                  {(uploadFile.size / 1024).toFixed(2)} KB)
                </div>
              )}

              <Button variant='primary' onClick={handleUpload} disabled={!uploadFile || loading}>
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className='bi bi-cloud-upload me-2'></i>
                    Upload & Import
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Import Insentif Excel */}
        <Col md={12} className='mb-5'>
          <Card>
            <Card.Header>
              <Card.Title className='fw-bold fs-3'>
                <i className='bi bi-cloud-arrow-up text-info me-2'></i>
                Import Excel Insentif (Upload Hasil Edit)
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <p className='text-muted mb-4'>
                Upload file Excel yang sudah diedit di luar sistem (hasil dari export template).
                Import tetap lanjut sampai baris terakhir walaupun ada error.
              </p>

              <Alert variant='info' className='mb-4'>
                <h6 className='alert-heading'>
                  <i className='bi bi-info-circle me-2'></i>
                  Validasi Otomatis:
                </h6>
                <ul className='mb-0 small'>
                  <li>Kolom bertanda (*) wajib diisi</li>
                  <li>Peserta diidentifikasi dari NIK dan Email</li>
                  <li>Jika uraian mengandung <strong>"insentif"</strong>: wajib laporan minggu 1-7 dan file bermaterai</li>
                  <li>Jika uraian mengandung <strong>"transportasi"</strong>: wajib file bermaterai</li>
                  <li>Data valid akan disimpan, data error akan dilewati</li>
                </ul>
              </Alert>

              <Form.Group className='mb-3'>
                <Form.Label>Pilih File Excel *</Form.Label>
                <Form.Control
                  id='insentifFileInput'
                  type='file'
                  accept='.xlsx,.xls,.csv'
                  onChange={handleInsentifFileChange}
                />
                <Form.Text className='text-muted'>
                  Format: .xlsx, .xls, atau .csv | Max: 10MB
                </Form.Text>
              </Form.Group>

              {insentifUploadFile && (
                <div className='alert alert-success mb-4'>
                  <i className='bi bi-file-earmark-check me-2'></i>
                  <strong>File dipilih:</strong> {insentifUploadFile.name} (
                  {(insentifUploadFile.size / 1024).toFixed(2)} KB)
                </div>
              )}

              <Button 
                variant='info' 
                onClick={handleImportInsentif} 
                disabled={!insentifUploadFile || loading}
              >
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Importing...
                  </>
                ) : (
                  <>
                    <i className='bi bi-cloud-arrow-up me-2'></i>
                    Import Insentif
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Export Insentif ERP */}
        <Col md={12} className='mb-5'>
          <Card>
            <Card.Header>
              <Card.Title className='fw-bold fs-3'>
                <i className='bi bi-file-earmark-spreadsheet text-warning me-2'></i>
                Export Excel Insentif ERP
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <p className='text-muted mb-4'>
                Export data insentif bulanan untuk sistem ERP. Masukkan uraian untuk nama file
                export.
              </p>

              <Form.Group className='mb-4'>
                <Form.Label>Uraian Insentif *</Form.Label>
                <Form.Control
                  type='text'
                  placeholder='Contoh: Insentif Bulanan Jan 2026 - Feb 2026'
                  value={insentifUraian}
                  onChange={(e) => setInsentifUraian(e.target.value)}
                />
                <Form.Text className='text-muted'>
                  Uraian ini akan muncul di file Excel yang diexport
                </Form.Text>
              </Form.Group>

              <Button
                variant='warning'
                onClick={handleExportInsentif}
                disabled={!insentifUraian.trim() || loading}
              >
                {loading ? (
                  <>
                    <Spinner animation='border' size='sm' className='me-2' />
                    Exporting...
                  </>
                ) : (
                  <>
                    <i className='bi bi-file-earmark-arrow-down me-2'></i>
                    Export Insentif
                  </>
                )}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Info Card */}
      <Card className='bg-light-info'>
        <Card.Body>
          <h5 className='mb-3'>
            <i className='bi bi-info-circle text-info me-2'></i>
            Catatan Penting
          </h5>
          <ul className='mb-0'>
            <li>Pastikan format Excel sesuai dengan template yang didownload</li>
            <li>File assessment maksimal 5MB, file insentif maksimal 10MB</li>
            <li>Import insentif tetap lanjut sampai baris terakhir walaupun ada error</li>
            <li>Export insentif akan menghasilkan data yang siap diimport ke sistem ERP</li>
          </ul>
        </Card.Body>
      </Card>

      {/* Modal Hasil Import */}
      <Modal show={showResultModal} onHide={handleCloseResultModal} size='xl' scrollable>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className='bi bi-clipboard-data me-2'></i>
            Hasil Import Insentif
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {importResult && (
            <>
              {/* Summary */}
              <Row className='mb-4'>
                <Col md={6}>
                  <Alert variant='success'>
                    <h6 className='mb-0'>
                      <i className='bi bi-check-circle me-2'></i>
                      Berhasil: <strong>{importResult.success_count}</strong> baris
                    </h6>
                  </Alert>
                </Col>
                <Col md={6}>
                  <Alert variant='danger'>
                    <h6 className='mb-0'>
                      <i className='bi bi-x-circle me-2'></i>
                      Gagal: <strong>{importResult.error_count}</strong> baris
                    </h6>
                  </Alert>
                </Col>
              </Row>

              {/* Error Details */}
              {importResult.errors && importResult.errors.length > 0 ? (
                <>
                  <h5 className='mb-3 text-danger'>
                    <i className='bi bi-exclamation-triangle me-2'></i>
                    Detail Error ({importResult.errors.length} baris):
                  </h5>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table striped bordered hover size='sm'>
                      <thead className='table-dark' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          <th style={{ width: '80px' }}>Baris</th>
                          <th style={{ width: '150px' }}>NIK</th>
                          <th style={{ width: '200px' }}>Email</th>
                          <th style={{ width: '150px' }}>Nama</th>
                          <th style={{ width: '200px' }}>Uraian</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importResult.errors.map((error, index) => (
                          <tr key={index}>
                            <td className='text-center'>
                              <span className='badge bg-danger'>{error.excel_row}</span>
                            </td>
                            <td className='small'>{error.nik || '-'}</td>
                            <td className='small'>{error.email || '-'}</td>
                            <td className='small'>{error.nama_penerima || '-'}</td>
                            <td className='small'>{error.uraian || '-'}</td>
                            <td>
                              {error.errors && error.errors.length > 0 ? (
                                <ul className='mb-0 small text-danger'>
                                  {error.errors.map((err, idx) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </ul>
                              ) : (
                                <span className='text-muted'>-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <Alert variant='success' className='mb-0'>
                  <h5 className='alert-heading'>
                    <i className='bi bi-check-circle-fill me-2'></i>
                    Import Berhasil!
                  </h5>
                  <p className='mb-0'>
                    Semua data berhasil diimport tanpa error. Total {importResult.success_count} baris
                    data telah disimpan ke database.
                  </p>
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={handleCloseResultModal}>
            Tutup
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default ExcelManagementAdmin
